import React, { useState, useEffect } from 'react';
import defaultThemeData from '../../../utils/themeDefaults';
import { validateThemeData } from '../../../utils/themeValidator';

const mergeDefaults = (defaults, src) => {
    if (!defaults) return src || {};
    const out = Array.isArray(defaults) ? [] : {};
    // copy keys from defaults, but prefer values from src when present
    Object.keys(defaults).forEach((k) => {
      const dval = defaults[k];
      const sval = src && Object.prototype.hasOwnProperty.call(src, k) ? src[k] : undefined;
      if (sval === undefined || sval === null) {
        out[k] = dval;
      } else if (typeof dval === 'object' && dval !== null && typeof sval === 'object' && sval !== null && !Array.isArray(dval)) {
        out[k] = mergeDefaults(dval, sval);
      } else {
        out[k] = sval;
      }
    });
    // copy any extra keys from src that weren't in defaults
    if (src && typeof src === 'object') {
      Object.keys(src).forEach((k) => { if (!Object.prototype.hasOwnProperty.call(out, k)) out[k] = src[k]; });
    }
    return out;
};

export default function ThemeForm({ theme, onCancel, onSave }) {
  const [name, setName] = useState(theme?.name || '');
  const [description, setDescription] = useState(theme?.description || '');
  const [themeData, setThemeData] = useState(() => mergeDefaults(defaultThemeData, theme?.theme_data || {}));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(theme?.name || '');
    setDescription(theme?.description || '');
    setThemeData(mergeDefaults(defaultThemeData, theme?.theme_data || {}));
  }, [theme]);

  const setAtPath = (path, value) => {
    setThemeData((s) => {
      const copy = JSON.parse(JSON.stringify(s || {}));
      const parts = path.split('.');
      let cur = copy;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
      cur[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  // ...existing code... (file uploads removed - theme images are no longer handled here)

  const handleSubmit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    // ensure nested defaults are present before validation and submit
    const finalThemeData = mergeDefaults(defaultThemeData, themeData || {});
    // remove deprecated 'typography' node so we send only the new 'body' shape
    if (finalThemeData && Object.prototype.hasOwnProperty.call(finalThemeData, 'typography')) {
      try { delete finalThemeData.typography; } catch (e) { /* ignore */ }
    }
    const v = validateThemeData({ name, theme_data: finalThemeData });
    if (!v.valid) { setErrors(v.errors); return; }
    setSaving(true);
    try {
      // No file uploads anymore. Send JSON object with theme_data containing new color scheme.
      await onSave({ ...(theme || {}), name, description, theme_data: finalThemeData });
    } finally { setSaving(false); }
  };

  // no preview URLs or asset resolution needed for removed image uploads

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-start justify-center p-6 overflow-auto">
      <form onSubmit={handleSubmit} className="bg-white rounded shadow p-6 w-full max-w-3xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{theme?.id ? 'Edit Theme' : 'Create Theme'}</h3>
          <div>
            <button type="button" onClick={onCancel} className="mr-2">Cancel</button>
            <button type="submit" disabled={saving} className="px-3 py-1 rounded bg-blue-600 text-white">{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded p-2" />
            {errors.name && <div className="text-red-600 text-xs mt-1">{errors.name}</div>}
          </div>

          <div>
            <label className="block text-sm">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border rounded p-2" rows={3} />
          </div>

          {/* Logo and Background images removed - theme images are no longer set here. */}

          <div>
            <label className="block text-sm">Color Scheme (CSS variables)</label>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <label className="block text-xs">--color-primary</label>
                <input type="color" value={themeData.colors?.primary || '#091DF2'} onChange={(e) => setAtPath("colors.primary", e.target.value)} />
                <div className="text-xs text-gray-600 mt-1">For anchor buttons, primary buttons, icons, etc.</div>
              </div>
              <div>
                <label className="block text-xs">--color-primary-text</label>
                <input type="color" value={themeData.colors?.primary_text || '#FFFFFF'} onChange={(e) => setAtPath("colors.primary_text", e.target.value)} />
                <div className="text-xs text-gray-600 mt-1">Text color on primary buttons</div>
              </div>

              <div>
                <label className="block text-xs">--color-heading</label>
                <input type="color" value={themeData.colors?.heading || '#0F1347'} onChange={(e) => setAtPath("colors.heading", e.target.value)} />
                <div className="text-xs text-gray-600 mt-1">For main headings</div>
              </div>
              <div>
                <label className="block text-xs">--color-heading-rgb</label>
                <input value={themeData.colors?.heading_rgb || '15, 19, 71'} onChange={(e) => setAtPath("colors.heading_rgb", e.target.value)} className="w-full border rounded p-1" />
                <div className="text-xs text-gray-600 mt-1">For overlay effects (comma separated R,G,B)</div>
              </div>

              <div>
                <label className="block text-xs">--color-text-body</label>
                <input type="color" value={themeData.colors?.text_body || '#666984'} onChange={(e) => setAtPath("colors.text_body", e.target.value)} />
                <div className="text-xs text-gray-600 mt-1">Paragraph and secondary text color</div>
              </div>
              <div>
                <label className="block text-xs">--color-border</label>
                <input type="color" value={themeData.colors?.border || '#D1DAEB'} onChange={(e) => setAtPath("colors.border", e.target.value)} />
                <div className="text-xs text-gray-600 mt-1">Input border color</div>
              </div>

              <div className="col-span-2">
                <label className="block text-xs">--color-bg-light</label>
                <input type="color" value={themeData.colors?.bg_light || '#FFFFFF'} onChange={(e) => setAtPath("colors.bg_light", e.target.value)} />
                <div className="text-xs text-gray-600 mt-1">Light background surfaces</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm">Body Color</label>
              <input type="color" value={themeData.body?.color || '#111827'} onChange={(e) => setAtPath('body.color', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm">Body Font Family</label>
              <input value={themeData.body?.font_family || 'Inter, sans-serif'} onChange={(e) => setAtPath('body.font_family', e.target.value)} className="w-full border rounded p-2" />
            </div>
            <div>
              <label className="block text-sm">Body Font Size</label>
              <input value={themeData.body?.font_size || ''} onChange={(e) => setAtPath('body.font_size', e.target.value)} className="w-full border rounded p-2" />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
